import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChatContext } from 'stream-chat-react';
import Loader from './components/Loader';
import React from 'react';
import { Button } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { MemberListTile } from './components/MemberListTile';
import { useAuth } from '../../hooks/useAuth';
import { GroupChat } from '../../models/GroupChat';
import { groupRepository } from '../../repositories/GroupRepository';
import { useConfirm } from '../../hooks/useConfirm';
import { ButtonAlignement } from '../../components/ConfirmModal';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import branch from 'branch-sdk';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { ChatChannelType } from '../../models/ChatChannel';
import { formatAmount } from '../../utils/Utils';

interface ProfilePageGroupProps {
  channelType: string;
}

const ProfilePageGroup: React.FC<ProfilePageGroupProps> = ({channelType}) => {
  const navigate = useNavigate();
  const { tab, groupId } = useParams();
  const { client } = useChatContext();
  const [groupChat, setGroupChat] = useState<GroupChat | null>(null);
  const [groupChatLink, setGroupChatLink] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMembersLoading, setIsMembersLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const {pleb} = useAuth();
  const {openConfirm} = useConfirm();

  useEffect(() => {
    setIsLoading(true);
    setIsMembersLoading(true);

    groupRepository.getGroupDetails(groupId ?? '', channelType, pleb?.id ?? '', true)
      .then((group) => {
        setGroupChat(group);
        setError(null);
      })
      .catch((err) => {
        console.error('Error getting channel:', err);
        setError(err as Error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    groupRepository.getGroupDetails(groupId ?? '', channelType, pleb?.id ?? '', false)
      .then((group) => {
        setGroupChat(group);
        setError(null);
      })
      .catch((err) => {
        console.error('Error getting channel:', err);
        setError(err as Error);
      })
      .finally(() => {
        setIsLoading(false);
        setIsMembersLoading(false);
      });
  }, [groupId]);

  useEffect(() => {
    if(groupChat && channelType === ChatChannelType.Group) {
        const linkData = {
          data: {
            $og_title: groupChat.name,
            twitter_image_url: groupChat.image,
            $og_image_url: groupChat.image,
            $og_image_width: 128,
            $og_image_height: 128,
            $og_type: "website",
            $canonical_identifier: `group-chat/${groupChat.id}?join=true`,
            $canonical_url: `orangepillapp://opa/group-chat/${groupChat.id}?join=true`,
            $og_description: groupChat.name,
            id: groupChat.id,
            kind: "group-chat",
            join: "true",
            $creation_timestamp: Date.now(),
            $always_deeplink: true,
          },
        };
      
        try {
          branch.link(linkData, (err, link) => {
            if(err) {
              console.error('Error generating Branch link:', err);
            } else {
              setGroupChatLink(link);
            }
          });
        } catch (error) {
          console.error('Error generating Branch link:', error);
        }
    }
  }, [groupChat]);

  const showDialogDelete = () => {
    openConfirm({
      message: 'Are you sure you want to delete this chat?',
      buttons: [
        {label: 'Cancel', onClick: () => {}, variant: ButtonVariant.transparent},
        {label: 'Delete', onClick: () => {handleDeleteChat()}, variant: ButtonVariant.danger}
      ],
      buttonAlignement: ButtonAlignement.row
    });
  }

  const showDialogLeave = () => {
    openConfirm({
      message: 'Are you sure you want to leave this chat ?',
      buttons: [
        {label: 'Cancel', onClick: () => {}, variant: ButtonVariant.transparent},
        {label: 'Leave', onClick: () => {handleLeaveChat()}, variant: ButtonVariant.danger}
      ],
      buttonAlignement: ButtonAlignement.row
    });
  }

  const handleDeleteChat = async () => {
    try {
      if(groupChat?.channelId) {
        await client.deleteChannels([groupChat.channelId]);
        navigate('/home/chat');
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const handleLeaveChat = async () => {
    try {
      if(groupChat?.id && client.userID) {
        const channel = client.channel('messaging', groupChat.id);
        await channel.removeMembers([client.userID]);
        navigate('/home/chat');
      }
    } catch (error) {
      console.error('Error leaving chat:', error);
    }
  };

  const renderProfile = () => {
    if (isLoading) return <Loader />;
    if (error) return <div>Error loading chat details</div>;
    if (!groupChat) return null;

    return (
      <div className="p-4 flex flex-col">
        <div className="flex flex-col items-center mb-6">
          <img 
            src={groupChat.image}
            className="object-cover w-32 h-32 rounded-full p-2 bg-gradient-to-b from-brand-orange to-brand-light-orange"
            alt={groupChat.name}
          />

          <h1 className="mt-2 text-xl font-bold text-center">
            {groupChat.name}
          </h1>
          
          <div className="flex flex-row justify-between items-center w-full px-6 mt-3">
            <CopyToClipboard toCopy={groupChatLink ?? ''} className={`grow ${groupChatLink === null ? 'invisible' : ''}`}>
              <div 
                className="flex flex-row items-center gap-2"
              >
                <span className="material-symbols-outlined text-brand-orange">
                  link
                </span>
                <p className="text-lg text-brand-orange">
                  Invite via link
                </p>
              </div>
            </CopyToClipboard>

            <p className="text-lg">
              {
                groupChat.members.length > 0 && `${formatAmount(groupChat.members.length)} member${groupChat.members.length > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>

        <div className="flex flex-col">
          {isMembersLoading ? (
            <Loader />
          ) : (
            groupChat.members.map(member => (
              <MemberListTile
                key={member.id}
                member={member}
                admin={groupChat.admins.includes(member.id)}
                onTap={() => navigate(`/home/${tab}/user/${member.id}`)}
              />
            ))
          )}
        </div>

        <div className="flex flex-col items-center mt-4">
          {
            channelType === ChatChannelType.Group && (
              <div className="flex flex-row w-auto">
                {groupChat.adminPermissionsEnabled ? (
                  <Button 
                    variant={ButtonVariant.danger}
                    className="mt-1 w-auto px-4"
                    onClick={() => showDialogDelete()}
                  >
                    <i className="material-icons mr-2">delete</i>
                    Delete Chat
                  </Button>
                ) : (
                  <Button
                    variant={ButtonVariant.danger}
                    className="mt-1 w-auto px-4"
                    onClick={() => showDialogLeave()}
                  >
                    <i className="material-icons mr-2">logout</i>
                    Leave Chat
                  </Button>
                )}
              </div>
            )
          }
        </div>
        
      </div>
    );
  };


  return (
    <LayoutWithTopBar
      onClickPrevArrow={() => navigate(-1)}
      topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{groupChat?.name}</h1>
        </div>
      }
    >
      {renderProfile()}
    </LayoutWithTopBar>
  );
}

export default ProfilePageGroup;